import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { registerAiClient } from 'redux/sellers/actions';
import { ReactComponent as ReviewsIcon } from 'assets/icons/new/Chapters/Reviews.svg';

const MARKETPLACE_OPTIONS = [
    { value: 'ozon', label: 'Ozon', checked: true },
    { value: 'wb', label: 'Wildberries', checked: true },
    //{ value: 'yandexmarket', label: 'Яндекс Маркет' },
];

const CheckboxGroup: React.FC<{
    title: string;
    selected: string[];
    onChange: (value: string) => void;
}> = ({ title, selected, onChange }) => (
    <div className="mb-6">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{title}</p>
        <div className="space-y-2">
            {MARKETPLACE_OPTIONS.map(({ value, label }) => (
                <label key={value} className="flex items-center gap-3 cursor-pointer group">
                    <input
                        type="checkbox"
                        checked={selected.includes(value)}
                        onChange={() => onChange(value)}
                        className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                        {label}
                    </span>
                </label>
            ))}
        </div>
    </div>
);

const AiReviewsRegister: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const sellerId = useAppSelector(({ sellers }) => sellers.currentSeller.info.data?.id);

    const [core, setCore] = useState<string[]>(
        MARKETPLACE_OPTIONS.filter(opt => opt.checked).map(opt => opt.value)
    );
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleValue = (list: string[], value: string): string[] =>
        list.includes(value) ? list.filter(v => v !== value) : [...list, value];

    const handleRegister = async () => {
        if (!sellerId) return;
        setIsLoading(true);
        setError(null);
        try {
            await dispatch(registerAiClient(sellerId, core) as any);
            navigate(`/${sellerId}/ai-reviews`);
        } catch (e: any) {
            setError(e?.message ?? t('aiReviews_registerError'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center page-iframe px-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 max-w-lg w-full">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 dark:bg-gray-700 text-c-blue shrink-0">
                        <ReviewsIcon style={{ width: '20px', height: '20px' }} className="fill-current" />
                    </div>
                    <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                        {t('aiReviews_registerTitle')}
                    </h1>
                </div>

                <CheckboxGroup
                    title={t('aiReviews_checkboxTitle')}
                    selected={core}
                    onChange={(v) => setCore(prev => toggleValue(prev, v))}
                />

                {error && (
                    <p className="mb-4 text-sm text-red-500">{error}</p>
                )}

                <div className="flex items-center gap-3">
                    <button
                        onClick={handleRegister}
                        disabled={isLoading}
                        className="flex-1 inline-flex items-center justify-center px-6 py-2.5 bg-c-blue hover:opacity-90 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-opacity"
                    >
                        {isLoading ? t('aiReviews_loading') : t('aiReviews_registerButton')}
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        disabled={isLoading}
                        className="inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors"
                    >
                        {t('aiReviews_back')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AiReviewsRegister;
